import { useState } from 'react';
import { Archive, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiFetch, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { useQueryClient } from '@tanstack/react-query';
import { datumZeitDe } from '@/lib/format';

type Props = {
  bestellungId: number;
  bestellnr: string;
  istEndzustand: boolean;
  archiviertAm: string | null;
};

/**
 * „Jetzt archivieren" (E13, A10.6).
 *
 * WAS DER KNOPF IST — UND WAS NICHT
 *   Er zieht die Wartezeit vor. Archiviert wird ausschließlich, was
 *   ohnehin archiviert würde: eine Bestellung im Endzustand, sobald die
 *   Frist um ist. Wer ihn drückt, sagt „die brauche ich jetzt schon nicht
 *   mehr im Brett", nicht „die ist fertig".
 *
 *   Deshalb erscheint er an einer nicht abgeschlossenen Bestellung gar
 *   nicht. Der Server weist sie zusätzlich ab (409 kein_endzustand) — eine
 *   Regel, die nur in der Oberfläche steht, ist keine Regel über die
 *   Daten. Die Oberfläche soll nur nicht erst anbieten, was der Server
 *   ohnehin ablehnt.
 *
 * IST SIE SCHON IM ARCHIV, steht statt des Knopfes das Datum. Ein Knopf,
 * der zweimal dasselbe tut, sieht aus wie eine offene Aufgabe.
 */
export function ArchivKnopf({ bestellungId, bestellnr, istEndzustand, archiviertAm }: Props) {
  const [laeuft, setLaeuft] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  if (!istEndzustand && archiviertAm === null) return null;

  if (archiviertAm !== null) {
    return (
      <span
        className="inline-flex min-h-[44px] items-center gap-1.5 text-xs text-ink-faint"
        title={`Archiviert am ${datumZeitDe(archiviertAm)}`}
      >
        <Archive className="h-4 w-4" aria-hidden />
        Im Archiv
      </span>
    );
  }

  async function archivieren(): Promise<void> {
    if (laeuft) return; // Doppelklick-Schutz
    setLaeuft(true);
    try {
      await apiFetch('/bestellungen/archivieren.php', {
        method: 'POST',
        body: JSON.stringify({ id: bestellungId })
      });
      // Beides neu laden: die Bestellung selbst und die Liste, aus der
      // sie verschwindet.
      qc.invalidateQueries({ queryKey: ['bestellung', bestellnr] });
      qc.invalidateQueries({ queryKey: ['bestellungen'] });
      toast('success', 'Bestellung archiviert. Sie steht weiter im Archiv.');
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unbekannt';
      toast(
        'error',
        code === 'kein_endzustand'
          ? 'Nur abgeschlossene Bestellungen lassen sich archivieren.'
          : `Archivieren fehlgeschlagen: ${code}`
      );
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Button
 data-nutzung="jetzt_archivieren"      variant="secondary"
      data-tour="detail-archivieren"
      onClick={() => void archivieren()}
      disabled={laeuft}
      title="Aus dem Dashboard nehmen. Im Archiv bleibt sie auffindbar."
    >
      {laeuft ? <Check className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
      {laeuft ? 'Wird archiviert …' : 'Jetzt archivieren'}
    </Button>
  );
}
